import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkSession();
  }, []);

  const checkSession = async () => {
    try {
      // Try OAuth session first (uses cookie or Authorization header)
      const oauthResponse = await api.get('/auth/oauth/me');
      if (oauthResponse.data) {
        setUser(oauthResponse.data);
        setIsAuthenticated(true);
        setLoading(false);
        return;
      }
    } catch (error) {
      // OAuth session not found, try regular session
    }

    try {
      const response = await authAPI.getSession();
      if (response.data) {
        setUser(response.data);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const signin = async (credentials) => {
    const response = await authAPI.signin(credentials);
    // Store token in localStorage for Authorization header fallback
    if (response.data?.token) {
      localStorage.setItem('session_token', response.data.token);
    }
    setUser(response.data);
    setIsAuthenticated(true);
    return response.data;
  };

  const signup = async (userData) => {
    const response = await authAPI.signup(userData);
    if (response.data?.token) {
      localStorage.setItem('session_token', response.data.token);
    }
    setUser(response.data);
    setIsAuthenticated(true);
    return response.data;
  };

  const signout = async () => {
    try {
      await authAPI.signout();
    } catch (e) {}
    try {
      await api.post('/auth/oauth/logout');
    } catch (e) {}
    localStorage.removeItem('session_token');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Google OAuth login
  const signInWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const value = {
    user,
    setUser,
    loading,
    signin,
    signup,
    signout,
    signInWithGoogle,
    isAuthenticated,
    setIsAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
